import { useState, useEffect, useCallback, useMemo } from 'react';
import { type Address, formatUnits } from 'viem';
import BigNumber from 'bignumber.js';
import { useConnection, useReadContract, useReadContracts, useWriteContract } from 'wagmi';
import { chainSettings, type ChainType, fallbackChainId } from '@/constants/chain-specific';
import { DEX_LIST } from '@/constants/project-specific/farms/dex';
import { defaultNativeAddress } from '@/constants/chain-specific';
import { tokenAbi } from '@/constants/abi/tokenAbi';
import { chewyRouterAbi } from '@/constants/abi/chewyRouterAbi';

// Type definitions for getReserves return type
type ReservesResult = readonly [bigint, bigint, number];

// Type definitions
export type V2PositionType = {
  dexKey: string;
  pairAddress: Address;
  token0: Address;
  token1: Address;
  balance: {
    raw: bigint;
    formatted: string;
  };
  reserves?: {
    reserve0: bigint;
    reserve1: bigint;
  };
  totalSupply?: bigint;
  lpName?: string;
  lpSymbol?: string;
};
export type V3PositionType = {
  dexKey: string;
  tokenId: number;
  operator: Address;
  token0: Address;
  token1: Address;
  fee: number;
  tickLower: number;
  tickUpper: number;
  liquidity: BigNumber;
  feeGrowthInside0LastX128: BigNumber;
  feeGrowthInside1LastX128: BigNumber;
  tokensOwed0: BigNumber;
  tokensOwed1: BigNumber;
};
export type CollectFeesParams = {
  dexKey: string;
  tokenId: number;
  recipient?: Address;
};

// New type for approval handling
export type ApprovalStatus = 'pending' | 'approved' | 'error';
export type SwapTaxOnTransferParams = {
  path: Address[];
  amountIn?: bigint;
  amountOutMin: bigint;
  recipient?: Address;
  deadline?: bigint;
  dexKey: string;
};
export type SwapTaxOnTransferResult = {
  swapTokensForTokens: (params: SwapTaxOnTransferParams) => Promise<string | undefined>;
  swapETHForTokens: (params: SwapTaxOnTransferParams) => Promise<string | undefined>;
  swapTokensForETH: (params: SwapTaxOnTransferParams) => Promise<string | undefined>;
  isPending: boolean;
  error: Error | null;
};

// Helper function to make BigInt serializable
const serializeBigInt = (obj: any): any => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  if (typeof obj === 'bigint') {
    return obj.toString();
  }
  if (Array.isArray(obj)) {
    return obj.map(serializeBigInt);
  }
  if (typeof obj === 'object') {
    return Object.fromEntries(Object.entries(obj).map(([key, value]) => [key, serializeBigInt(value)]));
  }
  return obj;
};

/**
 * Hook for swapping tax-on-transfer tokens through V2 DEX routers
 *
 * @param chainId - Chain ID to use for swaps
 * @returns Object with swap functions for different scenarios
 */
export function useSwapTaxOnTransfer(chainId: ChainType = fallbackChainId): SwapTaxOnTransferResult {
  const {
    address: userAddress
  } = useConnection();
  const {
    writeContractAsync,
    isPending,
    error
  } = useWriteContract();
  const [lastError, setLastError] = useState<Error | null>(null);

  // Function to approve token spending if needed
  const approveToken = useCallback(async (tokenAddress: Address, spenderAddress: Address, amount: bigint): Promise<boolean> => {
    try {
      // Skip approval for native token
      if (tokenAddress === defaultNativeAddress) {
        return true;
      }

      // Approve max amount
      const maxApproval = BigInt('0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff');
      await writeContractAsync({
        address: tokenAddress,
        abi: tokenAbi,
        functionName: 'approve',
        args: [spenderAddress, maxApproval],
        chainId
      });
      return true;
    } catch (error_0) {
      console.error('Error approving token:', error_0);
      setLastError(error_0 instanceof Error ? error_0 : new Error('Unknown error during token approval'));
      return false;
    }
  }, [chainId, writeContractAsync]);

  // Swap token to token with fee-on-transfer support
  const swapTokensForTokens = useCallback(async (params: SwapTaxOnTransferParams): Promise<string | undefined> => {
    if (!userAddress) {
      throw new Error('No account connected');
    }
    const dex = DEX_LIST[params.dexKey];
    if (!dex) {
      throw new Error(`DEX ${params.dexKey} not found`);
    }

    // Validate path
    if (!params.path || params.path.length < 2) {
      throw new Error('Invalid swap path: must contain at least 2 token addresses');
    }

    // Required parameters
    if (!params.amountIn) {
      throw new Error('Amount in is required');
    }

    // Default deadline is 20 minutes from now
    const deadline = params.deadline || BigInt(Math.floor(Date.now() / 1000) + 1200);
    const recipient = params.recipient || userAddress;
    try {
      // Approve token if needed
      const isApproved = await approveToken(params.path[0], dex.router.address, params.amountIn);
      if (!isApproved) {
        throw new Error('Failed to approve token');
      }

      // Execute swap
      const tx = await writeContractAsync({
        address: dex.router.address,
        abi: chewyRouterAbi,
        functionName: 'swapExactTokensForTokensSupportingFeeOnTransferTokens',
        args: [params.amountIn, params.amountOutMin, params.path, recipient, deadline],
        chainId
      });
      return tx;
    } catch (err) {
      console.error('Error swapping tokens:', err);
      setLastError(err instanceof Error ? err : new Error('Unknown error during swap'));
      throw err;
    }
  }, [userAddress, chainId, writeContractAsync, approveToken]);

  // Swap ETH to token with fee-on-transfer support
  const swapETHForTokens = useCallback(async (params_0: SwapTaxOnTransferParams): Promise<string | undefined> => {
    if (!userAddress) {
      throw new Error('No account connected');
    }
    const dex_0 = DEX_LIST[params_0.dexKey];
    if (!dex_0) {
      throw new Error(`DEX ${params_0.dexKey} not found`);
    }

    // Validate path
    if (!params_0.path || params_0.path.length < 2) {
      throw new Error('Invalid swap path: must contain at least 2 token addresses');
    }

    // Verify the first token is WETH/native token
    if (params_0.path[0] !== chainSettings[chainId]?.wNative?.address) {
      throw new Error('First token in path must be WETH for ETH to token swaps');
    }

    // Default deadline is 20 minutes from now
    const deadline_0 = params_0.deadline || BigInt(Math.floor(Date.now() / 1000) + 1200);
    const recipient_0 = params_0.recipient || userAddress;
    try {
      // No approval needed for native ETH
      const tx_0 = await writeContractAsync({
        address: dex_0.router.address,
        abi: chewyRouterAbi,
        functionName: 'swapExactETHForTokensSupportingFeeOnTransferTokens',
        args: [params_0.amountOutMin, params_0.path, recipient_0, deadline_0],
        value: params_0.amountIn,
        chainId
      });
      return tx_0;
    } catch (err_0) {
      console.error('Error swapping ETH for tokens:', err_0);
      setLastError(err_0 instanceof Error ? err_0 : new Error('Unknown error during swap'));
      throw err_0;
    }
  }, [userAddress, chainId, writeContractAsync]);

  // Swap token to ETH with fee-on-transfer support
  const swapTokensForETH = useCallback(async (params_1: SwapTaxOnTransferParams): Promise<string | undefined> => {
    if (!userAddress) {
      throw new Error('No account connected');
    }
    const dex_1 = DEX_LIST[params_1.dexKey];
    if (!dex_1) {
      throw new Error(`DEX ${params_1.dexKey} not found`);
    }

    // Validate path
    if (!params_1.path || params_1.path.length < 2) {
      throw new Error('Invalid swap path: must contain at least 2 token addresses');
    }

    // Verify the last token is WETH/native token
    if (params_1.path[params_1.path.length - 1] !== chainSettings[chainId]?.wNative?.address) {
      throw new Error('Last token in path must be WETH for token to ETH swaps');
    }

    // Required parameters
    if (!params_1.amountIn) {
      throw new Error('Amount in is required');
    }

    // Default deadline is 20 minutes from now
    const deadline_1 = params_1.deadline || BigInt(Math.floor(Date.now() / 1000) + 1200);
    const recipient_1 = params_1.recipient || userAddress;
    try {
      // Approve token if needed
      const isApproved_0 = await approveToken(params_1.path[0], dex_1.router.address, params_1.amountIn);
      if (!isApproved_0) {
        throw new Error('Failed to approve token');
      }

      // Execute swap
      const tx_1 = await writeContractAsync({
        address: dex_1.router.address,
        abi: chewyRouterAbi,
        functionName: 'swapExactTokensForETHSupportingFeeOnTransferTokens',
        args: [params_1.amountIn, params_1.amountOutMin, params_1.path, recipient_1, deadline_1],
        chainId
      });
      return tx_1;
    } catch (err_1) {
      console.error('Error swapping tokens for ETH:', err_1);
      setLastError(err_1 instanceof Error ? err_1 : new Error('Unknown error during swap'));
      throw err_1;
    }
  }, [userAddress, chainId, writeContractAsync, approveToken]);
  return {
    swapTokensForTokens,
    swapETHForTokens,
    swapTokensForETH,
    isPending,
    error: error || lastError
  };
}